import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import "../../../App.css";

import {
  X,
  User,
  Mail,
  Phone,
  Briefcase,
  Award,
  Code,
  Send,
  CheckCircle,
  AlertCircle,
  FileText,
} from "lucide-react";

import { uploadResumeToCloudinary } from "../../../utils/uploadResumeToCloudinary";
import { sendJobApplication } from "../../../utils/emailService";

/* ============================================================
   INPUT COMPONENT
============================================================ */

const Input = ({ icon, error, children }) => (
  <div className="space-y-1">
    <div
      className={`
        flex
        items-center
        rounded-xl
        border
        theme-card-secondary
        transition-all
        duration-300
        ${
          error
            ? "border-red-500"
            : `
              theme-border
              focus-within:border-[var(--brand-primary)]
              focus-within:ring-1
              focus-within:ring-[var(--brand-primary)]/30
            `
        }
      `}
    >
      {/* Input Icon */}
      <div className="flex-shrink-0 px-3 brand-color">
        {icon}
      </div>

      {children}
    </div>

    {/* Error */}
    {error && (
      <p className="flex items-center gap-1 text-xs text-red-400">
        <AlertCircle size={14} />
        {error}
      </p>
    )}
  </div>
);

/* ============================================================
   JOB APPLICATION MODAL
============================================================ */

const JobApplicationModal = ({ isOpen, onClose, job }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    jobTitle: "",
    experience: "",
    skills: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [resume, setResume] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [success, setSuccess] = useState(false);

  /* ============================================================
     LOAD JOB DATA
  ============================================================ */

  useEffect(() => {
    if (job) {
      setFormData((prev) => ({
        ...prev,
        jobTitle: job.title || "",
        experience: job.experience || "",
        skills: Array.isArray(job.skills)
          ? job.skills.join(", ")
          : job.skills || "",
      }));
    }
  }, [job]);

  /* ============================================================
     VALIDATION
  ============================================================ */

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Applicant name is required.";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required.";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Enter a valid email.";
    }

    if (!formData.phone.trim()) {
      newErrors.phone = "Phone number is required.";
    } else if (!/^\d{10}$/.test(formData.phone)) {
      newErrors.phone = "Enter a valid 10-digit phone number.";
    }

    if (!formData.experience.trim()) {
      newErrors.experience = "Experience is required.";
    }

    if (!formData.skills.trim()) {
      newErrors.skills = "Skills are required.";
    }

    if (!resume) {
      newErrors.resume = "Resume is required.";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  /* ============================================================
     HANDLE INPUT CHANGE
  ============================================================ */

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  /* ============================================================
     RESET FORM
  ============================================================ */

  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      phone: "",
      jobTitle: job?.title || "",
      experience: job?.experience || "",
      skills: Array.isArray(job?.skills)
        ? job.skills.join(", ")
        : job?.skills || "",
    });

    setErrors({});
    setResume(null);
  };

  /* ============================================================
     SUBMIT APPLICATION
  ============================================================ */

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    setLoading(true);

    try {
      let resumeData = null;

      /* Upload Resume */
      if (resume) {
        setUploading(true);

        resumeData = await uploadResumeToCloudinary(resume);

        setUploading(false);
      }

      /* Send Application */
      const result = await sendJobApplication({
        ...formData,
        resume: resumeData?.url || "",
      });

      if (result.success) {
        setSuccess(true);

        setTimeout(() => {
          setSuccess(false);
          resetForm();
          setResume(null);
          onClose();
        }, 3000);
      } else {
        alert("Unable to submit application. Please try again.");
      }
    } catch (error) {
      console.error(error);
      alert("Failed to upload resume.");
    } finally {
      setLoading(false);
      setUploading(false);
    }
  };

  /* ============================================================
     CLOSE WHEN NOT OPEN
  ============================================================ */

  if (!isOpen) return null;

  /* ============================================================
     MODAL
  ============================================================ */

  const modal = (
    <div
      className="
        fixed
        inset-0
        z-[999]
        flex
        items-center
        justify-center
        bg-black/75
        backdrop-blur-md
        p-3
        sm:p-5
      "
    >
      {/* ========================================================
          MODAL CONTAINER
      ======================================================== */}

      <div
        className="
          theme-card
          w-full
          max-w-md
          overflow-hidden
          rounded-2xl
          border
          theme-border
          shadow-[0_20px_70px_var(--shadow-color)]
          sm:max-w-lg
          lg:max-w-xl
          lg:rounded-3xl
          max-h-[92vh]
          flex
          flex-col
        "
      >
        {/* ======================================================
            HEADER
        ====================================================== */}

        <div
          className="
            sticky
            top-0
            z-20
            flex
            items-start
            justify-between
            gap-4
            border-b
            theme-border
            theme-card
            px-5
            py-4
            sm:px-6
            sm:py-5
          "
        >
          {/* Header Content */}
          <div className="min-w-0">
            <h2
              className="
                text-xl
                font-bold
                theme-text-primary
                sm:text-2xl
              "
            >
              Apply for Job
            </h2>

            <p
              className="
                mt-1
                text-xs
                leading-relaxed
                theme-text-muted
                sm:text-sm
              "
            >
              Fill in your details to submit your application.
            </p>
          </div>

          {/* Close Button */}
          <button
            type="button"
            onClick={onClose}
            className="
              flex
              h-10
              w-10
              flex-shrink-0
              items-center
              justify-center
              rounded-full
              theme-bg-tertiary
              theme-text-secondary
              transition-all
              duration-300
              hover:bg-[var(--brand-primary)]
              hover:text-white
            "
            aria-label="Close application modal"
          >
            <X size={18} />
          </button>
        </div>

        {/* ======================================================
            BODY
        ====================================================== */}

        <div className="custom-scrollbar flex-1 overflow-y-auto">
          {success ? (
            /* ==================================================
               SUCCESS STATE
            ================================================== */

            <div
              className="
                px-6
                py-10
                text-center
                sm:px-8
                sm:py-12
              "
            >
              <CheckCircle
                size={60}
                className="
                  mx-auto
                  brand-color
                  sm:h-[70px]
                  sm:w-[70px]
                "
              />

              <h3
                className="
                  mt-5
                  text-xl
                  font-bold
                  theme-text-primary
                  sm:text-2xl
                "
              >
                Application Submitted
              </h3>

              <p
                className="
                  mx-auto
                  mt-3
                  max-w-md
                  text-sm
                  leading-7
                  theme-text-muted
                  sm:text-base
                "
              >
                Thank you for applying. Our HR team will contact you if
                your profile matches the requirements.
              </p>
            </div>
          ) : (
            /* ==================================================
               APPLICATION FORM
            ================================================== */

            <form
              onSubmit={handleSubmit}
              className="
                space-y-4
                px-5
                py-5
                sm:px-6
                sm:py-6
              "
            >
              {/* ==================================================
                  APPLICANT NAME
              ================================================== */}

              <Input
                icon={<User size={18} />}
                error={errors.name}
              >
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Applicant Name"
                  className="
                    w-full
                    bg-transparent
                    py-3
                    pr-4
                    text-sm
                    theme-text-primary
                    outline-none
                    placeholder:text-[var(--text-muted)]
                    sm:text-base
                  "
                />
              </Input>

              {/* ==================================================
                  EMAIL
              ================================================== */}

              <Input
                icon={<Mail size={18} />}
                error={errors.email}
              >
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email Address"
                  className="
                    w-full
                    bg-transparent
                    py-3
                    pr-4
                    text-sm
                    theme-text-primary
                    outline-none
                    placeholder:text-[var(--text-muted)]
                    sm:text-base
                  "
                />
              </Input>

              {/* ==================================================
                  PHONE
              ================================================== */}

              <Input
                icon={<Phone size={18} />}
                error={errors.phone}
              >
                <input
                  type="tel"
                  maxLength={10}
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Phone Number"
                  className="
                    w-full
                    bg-transparent
                    py-3
                    pr-4
                    text-sm
                    theme-text-primary
                    outline-none
                    placeholder:text-[var(--text-muted)]
                    sm:text-base
                  "
                />
              </Input>

              {/* ==================================================
                  JOB TITLE
              ================================================== */}

              <Input icon={<Briefcase size={18} />}>
                <input
                  type="text"
                  value={job?.title || ""}
                  readOnly
                  className="
                    w-full
                    cursor-not-allowed
                    bg-transparent
                    py-3
                    pr-4
                    text-sm
                    font-medium
                    theme-text-primary
                    outline-none
                    sm:text-base
                  "
                />
              </Input>

              {/* ==================================================
                  EXPERIENCE
              ================================================== */}

              <Input
                icon={<Award size={18} />}
                error={errors.experience}
              >
                <input
                  type="text"
                  value={formData.experience}
                  readOnly
                  className="
                    w-full
                    cursor-not-allowed
                    bg-transparent
                    py-3
                    pr-4
                    text-sm
                    theme-text-primary
                    outline-none
                    sm:text-base
                  "
                />
              </Input>

              {/* ==================================================
                  SKILLS
              ================================================== */}

              <Input
                icon={<Code size={18} />}
                error={errors.skills}
              >
                <div
                  className="
                    flex
                    min-h-[52px]
                    w-full
                    items-center
                    pr-4
                    text-sm
                    theme-text-primary
                    sm:text-base
                  "
                >
                  {formData.skills}
                </div>
              </Input>

              {/* ==================================================
                  RESUME
              ================================================== */}

              <Input
                icon={<FileText size={18} />}
                error={errors.resume}
              >
                <input
                  type="file"
                  accept=".pdf,.doc,.docx"
                  onChange={(e) => {
                    setResume(e.target.files?.[0] || null);

                    if (errors.resume) {
                      setErrors((prev) => ({
                        ...prev,
                        resume: "",
                      }));
                    }
                  }}
                  className="
                    w-full
                    cursor-pointer
                    bg-transparent
                    py-3
                    pr-4
                    text-sm
                    theme-text-primary
                    file:mr-3
                    file:cursor-pointer
                    file:rounded-md
                    file:border-0
                    file:bg-[var(--brand-primary)]
                    file:px-3
                    file:py-2
                    file:text-white
                    file:transition-all
                    file:duration-300
                    hover:file:bg-[var(--brand-hover)]
                    sm:text-base
                  "
                />
              </Input>

              {/* ==================================================
                  SUBMIT BUTTON
              ================================================== */}

              <button
                type="submit"
                disabled={loading}
                className="
                  mt-2
                  flex
                  w-full
                  items-center
                  justify-center
                  gap-2
                  rounded-xl
                  bg-[var(--brand-primary)]
                  py-3
                  text-sm
                  font-semibold
                  text-white
                  transition-all
                  duration-300
                  hover:bg-[var(--brand-hover)]
                  hover:shadow-[0_8px_25px_rgba(249,115,22,0.25)]
                  disabled:cursor-not-allowed
                  disabled:opacity-60
                  sm:py-3.5
                  sm:text-base
                "
              >
                {loading ? (
                  <>
                    <div
                      className="
                        h-5
                        w-5
                        animate-spin
                        rounded-full
                        border-2
                        border-white
                        border-t-transparent
                      "
                    />

                    {uploading ? "Uploading Resume..." : "Sending..."}
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Submit Application
                  </>
                )}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );

  /* ============================================================
     PORTAL
  ============================================================ */

  return ReactDOM.createPortal(modal, document.body);
};

export default JobApplicationModal;